/**
 * Reusable icon+tooltip status indicator backed by one canonical config, in two
 * shapes:
 *  - `badge` (default): the 18px Check / Loader2 / AlertCircle circle the
 *    Document Library shows as a row's leading glyph.
 *  - `dot`: a compact corner dot for the home Recent Documents/Scopes rows,
 *    where the type icon leads. It renders for every mapped status so status is
 *    always visible — pulsing for in-progress, a calm fill for success, and the
 *    destructive fill for failed.
 *
 * Renders `null` only for an unknown `(type, status)` pair or a null status, so
 * callers can compose it with an icon fallback (see `InlineStatusIndicatorOrIcon`)
 * or overlay it on a type icon.
 */
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
    getStatusConfig,
    INDICATOR_STYLES,
    type StatusItemType,
} from "./inlineStatusIndicator.config";

export function InlineStatusIndicator({
    type,
    status,
    variant = "badge",
    className,
}: {
    type: StatusItemType;
    status: string | null;
    variant?: "badge" | "dot";
    className?: string;
}) {
    const config = getStatusConfig(type, status);
    if (!config) return null;

    const styles = INDICATOR_STYLES[config.indicator];
    const Icon = styles.Icon;

    const trigger =
        variant === "dot" ? (
            <span
                data-status={config.indicator}
                aria-label={config.title}
                className={cn(
                    "block size-2.5 rounded-full",
                    styles.dot,
                    config.indicator === "processing" && "animate-pulse",
                    className
                )}
            />
        ) : (
            <span
                data-status={config.indicator}
                aria-label={config.title}
                className={cn(
                    "inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border",
                    styles.badge,
                    className
                )}
            >
                <Icon
                    size={10}
                    strokeWidth={2.5}
                    className={styles.iconClassName}
                    aria-hidden
                />
            </span>
        );

    return (
        <TooltipProvider delay={200}>
            <Tooltip>
                <TooltipTrigger render={trigger} />
                <TooltipContent variant="rich" side="bottom">
                    <div className="flex items-center gap-1.5">
                        <Icon
                            size={13}
                            strokeWidth={2.5}
                            className={cn(
                                styles.titleClassName,
                                styles.iconClassName
                            )}
                            aria-hidden
                        />
                        <p
                            className={cn(
                                "text-[0.8125rem] leading-none font-semibold",
                                styles.titleClassName
                            )}
                        >
                            {config.title}
                        </p>
                    </div>
                    <p className="text-muted-foreground text-xs leading-snug">
                        {config.description}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
